import { useEffect, useState } from "react";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { getBondCatalog } from "@/lib/brokerBonds";
import { sentenceCaseKey } from "@/lib/listFormat";

// BRK-S179 — turning a bond's TYPE KEY into words.
//
// The issued-bonds row publishes `bondType` as the discovery catalogue's
// subproduct KEY (`bid_bond`), not as a label, and PMS says why in the contract
// itself: "the catalogue lives in discovery and PMS is not the authority on
// what it holds, so label it from `GET /api/broker/bond/catalog`." Rendering
// the key raw puts `bid_bond` on a page a broker hands an obligee — which is
// exactly the class of defect the wave-1 re-pin surfaced, and which no unit
// test written against the brief could have caught.
//
// ── A LABEL IS NEVER A REASON TO DROP A ROW ─────────────────────────────────
//
// The catalogue is a second read, and it can fail, be slow, or simply not hold
// the key (an issuance whose subproduct was retired). All three degrade the
// same way: the key is humanised locally (`bid_bond` → "Bid bond") and the bond
// still lists. A row is a record of something Signal issued; a missing label is
// a worse day than a good one and not a reason to hide it.
//
// Deliberately NOT cached across mounts, for the reason
// `usePreapprovalSubproductId` gives: the catalogue is a small read the picker
// already makes, and a module-level cache would be client state PMS is the
// record for (NN #9).

/** `bid_bond` → "Bid bond" — the fallback, and the shape of the real labels. */
export function humaniseBondType(key: string): string {
  return sentenceCaseKey(key);
}

/**
 * A `(bondType) => label` resolver backed by the catalogue, with the local
 * humanisation as its floor.
 *
 * Returns a function rather than a map so a caller cannot accidentally render
 * `undefined` for a key the catalogue does not hold.
 */
export function useBondTypeLabels(
  request: AuthedRequester,
): (key: string) => string {
  const [labels, setLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await getBondCatalog(request);
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const product of page.items) {
          for (const sub of product.subproducts) {
            // `key` is optional on the catalogue's own shape — a subproduct
            // without one simply contributes no label rather than a `undefined`
            // entry that would shadow the fallback.
            if (sub.key) next[sub.key] = sub.name;
          }
        }
        setLabels(next);
      } catch {
        // Silent: the bonds list is the page's content and the catalogue is
        // decoration on it. An error banner here would report a failure about
        // something the broker did not ask for.
        if (!cancelled) setLabels({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request]);

  return (key: string) => labels[key] ?? humaniseBondType(key);
}
